/**
 * FileDropZone — zone drag-drop multi-fichiers + click-to-browse.
 *
 *   <FileDropZone
 *     accept="image/jpeg,image/x-canon-cr2,...,.cr2,.cr3,.nef,.arw,.dng,.raf,.orf"
 *     maxFiles={10}
 *     value={files}
 *     onChange={setFiles}
 *   />
 *
 * Comportement :
 *  - Click ouvre le picker natif (input file masqué).
 *  - Drag-over surface highlight ; drop ajoute les fichiers (cumulatif).
 *  - Chaque fichier liste avec un bouton ✕ pour retirer.
 *  - Limite `maxFiles` = bloque l'ajout supplémentaire (silencieux).
 *
 * Le composant est `controlled` — la page parente owne le state
 * `files: File[]` et l'envoie ensuite via `multipart/form-data`.
 */

import { useRef, useState, type ChangeEvent, type DragEvent } from 'react'
import { Upload, X } from 'lucide-react'

import './FileDropZone.css'

interface FileDropZoneProps {
  value: File[]
  onChange: (files: File[]) => void
  /** Filtres natifs `<input accept="...">`. */
  accept?: string
  maxFiles?: number
  ariaLabel?: string
  className?: string
  /**
   * Texte d'aide affiché sous le bouton drop. Si omis (et `accept` posé),
   * fallback sur le hint œuvres "JPG, RAW (CR2/CR3/...)" pour ne pas casser
   * les usages historiques de `<NewWorkPage>`.
   */
  hint?: string
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ko`
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`
}

export default function FileDropZone({
  value,
  onChange,
  accept,
  maxFiles = 10,
  ariaLabel = 'Glisser-déposer des fichiers ici, ou cliquer pour parcourir',
  className,
  hint,
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const addFiles = (incoming: FileList | null | undefined) => {
    if (!incoming || incoming.length === 0) return
    const next: File[] = [...value]
    for (let i = 0; i < incoming.length; i += 1) {
      if (next.length >= maxFiles) break
      next.push(incoming[i])
    }
    onChange(next)
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files)
    // Reset l'input pour autoriser un re-pick du même fichier après un retrait.
    e.target.value = ''
  }

  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setDragOver(false)
    addFiles(e.dataTransfer.files)
  }

  const handleDragOver = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = () => setDragOver(false)

  const remove = (idx: number) => {
    const next = value.filter((_, i) => i !== idx)
    onChange(next)
  }

  const reachedMax = value.length >= maxFiles

  const classes = ['file-dropzone', dragOver && 'file-dropzone-drag', className]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes}>
      <button
        type="button"
        className="file-dropzone-surface"
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        aria-label={ariaLabel}
        disabled={reachedMax}
      >
        <Upload size={20} aria-hidden="true" />
        <span className="file-dropzone-label">
          {reachedMax
            ? `Maximum ${maxFiles} fichiers atteint.`
            : 'Glisser des fichiers ici, ou cliquer pour parcourir'}
        </span>
        {accept && (
          <span className="file-dropzone-hint">
            {hint ?? `JPG, RAW (CR2/CR3/NEF/ARW/DNG/RAF/ORF) — max ${maxFiles} fichiers`}
          </span>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        onChange={handleInputChange}
        className="file-dropzone-input"
        aria-hidden="true"
        tabIndex={-1}
      />

      {value.length > 0 && (
        <ul className="file-dropzone-list" role="list">
          {value.map((file, idx) => (
            <li key={`${file.name}-${idx}`} className="file-dropzone-item">
              <span className="file-dropzone-filename">{file.name}</span>
              <span className="file-dropzone-size ui-text-muted">{formatBytes(file.size)}</span>
              <button
                type="button"
                className="file-dropzone-remove"
                onClick={() => remove(idx)}
                aria-label={`Retirer ${file.name}`}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
